// Module-level cache so the FBT cards reappear instantly when the cart drawer
// re-renders (e.g. after a quantity change or an Add). Keyed by anchor product id.
const FBT_CACHE = {};

// Module-level cache for Fera ratings so they don't flicker on every cart re-render.
// Keyed by Shopify product id, value: { average, count } | null (if rated < 4)
const FERA_RATING_CACHE = {};

class CartRecommendations extends HTMLElement {
  constructor() {
    super();
    this._dragHandlers = null;
  }

  connectedCallback() {
    const anchorId = this.dataset.anchorProductId || '';
    const recsContainer = this.querySelector('[data-recommendations]');
    if (!recsContainer) return;

    // Cache hit — paint cached cards immediately so the section never goes blank.
    if (anchorId && FBT_CACHE[anchorId]) {
      recsContainer.innerHTML = FBT_CACHE[anchorId];
      this.bindCards();
      this.bindCarousel();
      this.hydrateFeraRatings();
    }

    // Always fetch fresh data in the background to keep the cache up to date.
    this.performRecommendations(anchorId);
  }

  disconnectedCallback() {
    if (this._dragHandlers) {
      document.removeEventListener('pointermove', this._dragHandlers.move);
      document.removeEventListener('pointerup', this._dragHandlers.up);
      document.removeEventListener('pointercancel', this._dragHandlers.up);
    }
  }

  performRecommendations(anchorId) {
    const recsContainer = this.querySelector('[data-recommendations]');
    if (!recsContainer) return;

    const tryFetch = url =>
      fetch(url)
        .then(r => r.text())
        .then(text => {
          const parsed = new DOMParser().parseFromString(text, 'text/html');
          const fetched = parsed.querySelector('[data-recommendations]');
          if (!fetched) return null;
          const html = fetched.innerHTML;
          if (!html || !html.trim()) return null;
          return html;
        });

    const apply = html => {
      if (anchorId) FBT_CACHE[anchorId] = html;
      recsContainer.innerHTML = html;
      // If the server pre-rendered this as hidden (fallback anchor with no
      // expected recs) but a valid complementary set actually came back,
      // un-hide so the cards become visible.
      this.classList.remove('is-empty');
      this.style.display = 'block';
      this.bindCards();
      this.bindCarousel();
      this.hydrateFeraRatings();
    };

    // Primary: complementary intent (merchant-curated in Search & Discovery).
    // Fallback: related intent (Shopify algorithmic) so the section is never empty.
    // Exception: when the anchor is a fallback excluded product (e.g. Linen
    // Sheet Set — the only thing in the cart), DON'T run the related fallback.
    // The merchant has explicitly chosen complementary curation as the source
    // of truth for these products; if no usable complementary recs exist, the
    // wrapper is hidden entirely instead of populating with algorithmic noise.
    const anchorIsFallback = this.dataset.anchorIsFallback === 'true';
    tryFetch(this.dataset.url)
      .then(html => {
        if (html) {
          apply(html);
          return;
        }
        if (anchorIsFallback) {
          this.classList.add('is-empty');
          this.style.display = 'none';
          return;
        }
        const fallbackUrl = this.dataset.url.replace(/intent=complementary/, 'intent=related');
        return tryFetch(fallbackUrl).then(fb => {
          if (fb) apply(fb);
        });
      })
      .catch(err => console.error('[fbt]', err));
  }

  bindCards() {
    this.querySelectorAll('.fbt-card').forEach(card => this.bindCard(card));
  }

  bindCard(card) {
    const productId = card.dataset.productId;
    const variantsScript = card.querySelector(
      `.fbt-variants-json[data-product-id="${productId}"]`
    );
    if (!variantsScript) return;

    let variants = [];
    try {
      variants = JSON.parse(variantsScript.textContent);
    } catch (e) {
      console.warn('[fbt] variant parse failed', e);
      return;
    }

    const optionSelects = Array.from(card.querySelectorAll('.fbt-option-select'));
    const addBtn = card.querySelector('.fbt-add-btn');
    const swatches = card.querySelectorAll('.fbt-swatch');
    const priceRow = card.querySelector('[data-fbt-price-row]');
    const pillWrap = card.querySelector('[data-fbt-pill-wrap]');
    const imageEl = card.querySelector('[data-fbt-image]');

    let currentColour = null;
    if (swatches.length > 0) {
      currentColour = swatches[0].dataset.colour;
    }

    // Match a variant against current colour + every dropdown selection.
    // Uses option1/option2/option3 (matched by position) for exact pairing
    // — much more reliable than scanning the variant.options array for substrings.
    const findVariant = () => {
      const exact = variants.find(v => {
        if (currentColour && !(v.options || []).includes(currentColour)) return false;
        for (const sel of optionSelects) {
          const pos = parseInt(sel.dataset.optionPosition, 10);
          const variantVal = v[`option${pos}`];
          if (variantVal !== sel.value) return false;
        }
        return true;
      });
      if (exact) return exact;
      // Fallback: relax constraints — match colour only, then any
      const colourOnly = variants.find(v =>
        currentColour ? (v.options || []).includes(currentColour) : true
      );
      return colourOnly || variants[0];
    };

    // Repopulate every OTHER dropdown so it only shows values that have a
    // valid variant given the current colour and the dropdown that just
    // changed. e.g. switching Filling 275gsm → 550gsm rewrites the Size
    // dropdown to only sizes that exist for 550gsm.
    const refreshOtherDropdowns = changedSelect => {
      optionSelects.forEach(sel => {
        if (sel === changedSelect) return;
        const pos = parseInt(sel.dataset.optionPosition, 10);
        const validValues = [];
        const seen = new Set();
        variants.forEach(v => {
          if (currentColour && !(v.options || []).includes(currentColour)) return;
          for (const otherSel of optionSelects) {
            if (otherSel === sel) continue;
            const otherPos = parseInt(otherSel.dataset.optionPosition, 10);
            if (v[`option${otherPos}`] !== otherSel.value) return;
          }
          const val = v[`option${pos}`];
          if (val != null && !seen.has(val)) {
            seen.add(val);
            validValues.push(val);
          }
        });
        if (validValues.length === 0) return;
        const previousVal = sel.value;
        sel.innerHTML = '';
        validValues.forEach(val => {
          const opt = document.createElement('option');
          opt.value = val;
          opt.textContent = String(val).split(' - ')[0].trim();
          if (val === previousVal) opt.selected = true;
          sel.appendChild(opt);
        });
        if (!validValues.includes(previousVal)) {
          sel.selectedIndex = 0;
        }
      });
    };

    // When colour changes, snap each dropdown to the first variant of the new
    // colour so the displayed selections are always achievable.
    const syncDropdownsToColour = () => {
      if (optionSelects.length === 0) return;
      const target = variants.find(v =>
        currentColour ? (v.options || []).includes(currentColour) : true
      ) || variants[0];
      if (!target) return;
      optionSelects.forEach(sel => {
        const pos = parseInt(sel.dataset.optionPosition, 10);
        const desiredVal = target[`option${pos}`];
        const exists = Array.from(sel.options).some(o => o.value === desiredVal);
        if (exists) sel.value = desiredVal;
      });
      // After snapping, re-run filter on each dropdown so dependent options stay tight
      optionSelects.forEach(sel => refreshOtherDropdowns(sel));
    };

    const computePeriod = isoDate => {
      if (!isoDate) return null;
      const parts = isoDate.split('-');
      if (parts.length < 3) return null;
      const day = parseInt(parts[2], 10);
      const monthIdx = parseInt(parts[1], 10) - 1;
      const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      let period;
      if (day <= 10) period = 'Early';
      else if (day <= 20) period = 'Mid';
      else period = 'Late';
      return { period, month: months[monthIdx] };
    };

    const buildPeriodLabel = (from, to) => {
      const f = computePeriod(from);
      const t = computePeriod(to);
      if (!f || !t) return '';
      if (f.period === t.period && f.month === t.month) {
        return `${f.period} ${f.month}`;
      }
      if (f.month === t.month) {
        return `${f.period} – ${t.period} ${f.month}`;
      }
      return `${f.period} ${f.month} – ${t.period} ${t.month}`;
    };

    const formatMoney = cents => {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && window.theme && window.theme.moneyFormat) {
        return window.Shopify.formatMoney(cents, window.theme.moneyFormat);
      }
      return '$' + (cents / 100).toFixed(2);
    };

    const updateCard = () => {
      const variant = findVariant();
      if (!variant) return;

      const isPreorder = variant.preorder_from && variant.preorder_to;
      const hasDiscount = variant.compare_at_price > variant.price;

      // Image swap (swatch click)
      if (imageEl && variant.featured_image) {
        imageEl.src = variant.featured_image;
      }

      if (priceRow) {
        let html = '';
        if (hasDiscount) {
          const savings = Math.round(
            ((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100
          );
          html =
            `<span style="font-size:12px;color:#a89880;text-decoration:line-through;font-family:'Harmonia Sans W01 Regular',Helvetica,Arial,sans-serif;">${formatMoney(variant.compare_at_price)}</span>` +
            `<span style="font-size:13px;color:#9D503E;font-weight:700;font-family:'Harmonia Sans W01 Bold','Harmonia Sans W01 Regular',Helvetica,Arial,sans-serif;">${formatMoney(variant.price)}</span>` +
            `<span style="font-size:8px;font-weight:700;letter-spacing:0.04em;text-transform:uppercase;color:#fff;background:#9D503E;padding:2px 6px;border-radius:10px;line-height:1.2;font-family:'Harmonia Sans W01 Bold','Harmonia Sans W01 Regular',Helvetica,Arial,sans-serif;">Save ${savings}%</span>`;
        } else {
          html =
            `<span style="font-size:13px;color:#2c2c2c;font-weight:700;font-family:'Harmonia Sans W01 Bold','Harmonia Sans W01 Regular',Helvetica,Arial,sans-serif;">${formatMoney(variant.price)}</span>`;
        }
        priceRow.innerHTML = html;
      }

      if (pillWrap) {
        if (isPreorder) {
          const label = buildPeriodLabel(variant.preorder_from, variant.preorder_to);
          pillWrap.innerHTML =
            `<div style="display:inline-flex;align-items:center;background:#9D503E;padding:3px 9px;border-radius:10px;">` +
            `<span style="font-size:9px;font-weight:700;letter-spacing:0.04em;text-transform:uppercase;color:#fff;line-height:1.2;font-family:'Harmonia Sans W01 Bold','Harmonia Sans W01 Regular',Helvetica,Arial,sans-serif;">Pre-order · ${label}</span>` +
            `</div>`;
        } else {
          pillWrap.innerHTML =
            `<div style="display:inline-flex;align-items:center;background:#ece4d8;padding:3px 9px;border-radius:10px;">` +
            `<span style="font-size:9px;font-weight:700;letter-spacing:0.04em;text-transform:uppercase;color:#5c4a35;line-height:1.2;font-family:'Harmonia Sans W01 Bold','Harmonia Sans W01 Regular',Helvetica,Arial,sans-serif;">In stock · Ships 1-2 days</span>` +
            `</div>`;
        }
      }

      if (addBtn) {
        addBtn.dataset.variantId = variant.id;
        addBtn.dataset.preorderFrom = variant.preorder_from || '';
        addBtn.dataset.preorderTo = variant.preorder_to || '';
        if (variant.available === false) {
          addBtn.disabled = true;
          addBtn.style.opacity = '0.5';
          addBtn.style.cursor = 'not-allowed';
        } else {
          addBtn.disabled = false;
          addBtn.style.opacity = '1';
          addBtn.style.cursor = 'pointer';
        }
      }
    };

    swatches.forEach(sw => {
      sw.addEventListener('click', e => {
        e.stopPropagation();
        swatches.forEach(s => {
          s.style.border = '1px solid #e0d6ca';
        });
        sw.style.border = '2px solid #8b7355';
        currentColour = sw.dataset.colour;
        syncDropdownsToColour();
        updateCard();
      });
    });

    optionSelects.forEach(sel => {
      sel.addEventListener('change', () => {
        refreshOtherDropdowns(sel);
        updateCard();
      });
      sel.addEventListener('click', e => e.stopPropagation());
    });

    if (addBtn) {
      addBtn.addEventListener('click', e => {
        e.stopPropagation();
        this.addCardToCart(card, addBtn);
      });
    }

    // Initial filter pass — make sure the dropdowns rendered by Liquid only
    // show options valid for the default colour selection.
    if (optionSelects.length > 0) {
      optionSelects.forEach(sel => refreshOtherDropdowns(sel));
    }
  }

  addCardToCart(card, addBtn) {
    const variantId = addBtn.dataset.variantId;
    if (!variantId || addBtn.disabled) return;

    addBtn.disabled = true;

    // ----- Immediate visual confirmation BEFORE the card slides away -----
    const originalBtnHtml = addBtn.innerHTML;
    addBtn.innerHTML =
      `<span style="display:inline-flex;align-items:center;gap:4px;font-size:10px;font-weight:700;color:#9D503E;line-height:1;font-family:'Harmonia Sans W01 Bold','Harmonia Sans W01 Regular',Helvetica,Arial,sans-serif;">` +
      `<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#9D503E" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>` +
      `ADDED!` +
      `</span>`;

    showCartToast('Item has been added to your cart!');

    // Single shared stamp (window.SelahPreorder) — byte-identical label across surfaces.
    const properties = window.SelahPreorder.properties(
      addBtn.dataset.preorderFrom,
      addBtn.dataset.preorderTo
    );

    const body = {
      items: [
        {
          id: parseInt(variantId, 10),
          quantity: 1,
          properties: properties
        }
      ],
      sections: ['cart-drawer', 'cart-counter'],
      sections_url: window.location.pathname
    };

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(body)
    })
      .then(r => r.json())
      .then(data => {
        if (data && data.sections) {
          // Hold the "ADDED!" state for ~600ms so the user actually registers
          // the confirmation BEFORE the card slides out and the drawer reloads.
          setTimeout(() => {
            const cardWidth = card.offsetWidth + 12; // gap
            card.classList.add('is-adding');
            card.style.marginRight = `-${cardWidth}px`;

            setTimeout(() => {
              const cartDrawer = document.querySelector('cart-drawer');
              if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
                cartDrawer.renderContents(data, true);
              }
              document.dispatchEvent(new CustomEvent('cart:change'));
            }, 260);
          }, 600);
        } else {
          // Failed: restore button state
          addBtn.innerHTML = originalBtnHtml;
          addBtn.disabled = false;
        }
      })
      .catch(err => {
        console.error('[fbt add]', err);
        addBtn.innerHTML = originalBtnHtml;
        addBtn.disabled = false;
      });
  }

  bindCarousel() {
    const scroller = this.querySelector('[data-fbt-scroll]');
    const prevBtn = this.querySelector('[data-fbt-arrow-prev]');
    const nextBtn = this.querySelector('[data-fbt-arrow-next]');
    const arrowsWrap = this.querySelector('[data-fbt-arrows]');
    if (!scroller) return;

    // Dedupe guard: bindCarousel runs once on cache hit and again after the
    // fresh fetch completes. The scroller and arrow elements live OUTSIDE the
    // swapped [data-recommendations] container so they persist across
    // re-renders — without this guard each bind would stack another set of
    // click + pointer listeners on the same elements.
    if (scroller.dataset.fbtCarouselBound === 'true') {
      // Just refresh visibility (in case the card count changed) and bail.
      const refresh = () => {
        const overflows = scroller.scrollWidth > scroller.clientWidth + 1;
        if (arrowsWrap) arrowsWrap.style.display = overflows ? 'flex' : 'none';
      };
      requestAnimationFrame(refresh);
      setTimeout(refresh, 400);
      return;
    }
    scroller.dataset.fbtCarouselBound = 'true';

    const updateArrowVisibility = () => {
      const overflows = scroller.scrollWidth > scroller.clientWidth + 1;
      if (arrowsWrap) {
        arrowsWrap.style.display = overflows ? 'flex' : 'none';
      }
      if (prevBtn) {
        const atStart = scroller.scrollLeft <= 1;
        prevBtn.style.opacity = atStart ? '0.4' : '1';
        prevBtn.style.cursor = atStart ? 'default' : 'pointer';
      }
      if (nextBtn) {
        const atEnd = scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 1;
        nextBtn.style.opacity = atEnd ? '0.4' : '1';
        nextBtn.style.cursor = atEnd ? 'default' : 'pointer';
      }
    };

    // Read each card's ACTUAL left position relative to the scroller's
    // content origin (using getBoundingClientRect, not offsetWidth math).
    // The previous math-based approach (`cardWidth = offsetWidth + 12px gap`)
    // accumulated drift each click because snap nudges scrollLeft to subpixel
    // positions that don't exactly match `index * cardWidth`. After ~4 clicks
    // the math diverged enough that target == current → no movement →
    // appeared unresponsive.
    const getCardOffsets = () => {
      const cards = Array.from(this.querySelectorAll('.fbt-card'));
      if (cards.length === 0) return [];
      const scRect = scroller.getBoundingClientRect();
      const currentScroll = scroller.scrollLeft;
      return cards.map(card => {
        const rect = card.getBoundingClientRect();
        return Math.round(rect.left - scRect.left + currentScroll);
      });
    };

    const snapToCard = direction => {
      const offsets = getCardOffsets();
      if (offsets.length === 0) return;
      const tolerance = 5;
      const currentScroll = scroller.scrollLeft;
      let target;
      if (direction === 'next') {
        target = offsets.find(off => off > currentScroll + tolerance);
      } else {
        // Largest offset that is to the LEFT of current scroll
        for (let i = offsets.length - 1; i >= 0; i--) {
          if (offsets[i] < currentScroll - tolerance) {
            target = offsets[i];
            break;
          }
        }
      }
      if (target == null) return; // already at start/end
      // Clamp to valid scroll range
      const maxScroll = scroller.scrollWidth - scroller.clientWidth;
      const clamped = Math.max(0, Math.min(maxScroll, target));
      scroller.scrollTo({ left: clamped, behavior: 'smooth' });
    };

    const cardWidth = () => {
      // Used by drag-suggested-card-width math elsewhere; still useful as a fallback.
      const firstCard = this.querySelector('.fbt-card');
      return firstCard ? firstCard.offsetWidth + 12 : 320;
    };

    if (prevBtn) {
      prevBtn.addEventListener('click', () => snapToCard('prev'));
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', () => snapToCard('next'));
    }

    scroller.addEventListener('scroll', updateArrowVisibility);
    requestAnimationFrame(updateArrowVisibility);
    setTimeout(updateArrowVisibility, 400);

    // ----- Mouse drag-to-scroll (NO setPointerCapture — that breaks child clicks) -----
    let isDown = false;
    let startX = 0;
    let startScroll = 0;
    let moved = false;
    const DRAG_THRESHOLD = 5;
    const self = this;

    const onPointerDown = e => {
      if (e.pointerType !== 'mouse') return;
      // Don't start a drag when the user clicks an interactive child element.
      const interactive = e.target.closest('button, a, select, .fbt-swatch');
      if (interactive) return;
      isDown = true;
      moved = false;
      startX = e.clientX;
      startScroll = scroller.scrollLeft;
    };

    const onPointerMove = e => {
      if (!isDown) return;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > DRAG_THRESHOLD) {
        if (!moved) {
          moved = true;
          self.classList.add('is-dragging');
        }
        scroller.scrollLeft = startScroll - dx;
        e.preventDefault();
      }
    };

    const onPointerUp = () => {
      if (!isDown) return;
      isDown = false;
      // Keep is-dragging class for one tick so the suppress-click handler can fire
      setTimeout(() => {
        self.classList.remove('is-dragging');
        moved = false;
      }, 0);
    };

    scroller.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerUp);
    document.addEventListener('pointercancel', onPointerUp);
    this._dragHandlers = { move: onPointerMove, up: onPointerUp };

    // Suppress click after a drag
    scroller.addEventListener('click', e => {
      if (moved) {
        e.stopPropagation();
        e.preventDefault();
      }
    }, true);

    // Prevent native HTML5 image drag entirely
    scroller.addEventListener('dragstart', e => e.preventDefault());

    // ----- Click-on-tile-to-slide -----
    // If a user clicks a card that is partially out of view, scroll so the
    // card is fully visible. Don't do this for the card that's already centered.
    scroller.addEventListener('click', e => {
      if (moved) return;
      const card = e.target.closest('.fbt-card');
      if (!card) return;
      // Only auto-slide when the click target is NOT an interactive child
      const interactive = e.target.closest('button, a, select, .fbt-swatch');
      if (interactive) return;
      const cardRect = card.getBoundingClientRect();
      const scRect = scroller.getBoundingClientRect();
      const partiallyHidden =
        cardRect.left < scRect.left - 4 || cardRect.right > scRect.right + 4;
      if (partiallyHidden) {
        const targetScroll =
          scroller.scrollLeft +
          (cardRect.left - scRect.left) -
          ((scRect.width - cardRect.width) / 2);
        scroller.scrollTo({ left: targetScroll, behavior: 'smooth' });
      }
    });
  }

  hydrateFeraRatings() {
    const placeholders = this.querySelectorAll('[data-fera-rating]');
    if (placeholders.length === 0) return;
    placeholders.forEach(el => {
      const productId = el.dataset.productId;
      if (!productId) return;
      // Apply cached rating immediately (zero flicker on re-render)
      if (Object.prototype.hasOwnProperty.call(FERA_RATING_CACHE, productId)) {
        applyFeraRating(el, FERA_RATING_CACHE[productId]);
      }
    });

    waitForFera()
      .then(fera => {
        if (!fera || !fera.api || typeof fera.api.retrieveProductRating !== 'function') return;
        placeholders.forEach(el => {
          const productId = el.dataset.productId;
          if (!productId) return;
          fera.api
            .retrieveProductRating(productId)
            .then(rating => {
              FERA_RATING_CACHE[productId] = rating || null;
              applyFeraRating(el, rating);
            })
            .catch(() => {});
        });
      })
      .catch(() => {});
  }
}

// ----- Shared Fera helpers (used by FBT cards AND cart line items) -----

function waitForFera() {
  return new Promise((resolve, reject) => {
    if (window.fera && window.fera.api && window.fera.api.retrieveProductRating) {
      return resolve(window.fera);
    }
    let attempts = 0;
    const tick = () => {
      attempts++;
      if (window.fera && window.fera.api && window.fera.api.retrieveProductRating) {
        return resolve(window.fera);
      }
      if (attempts > 60) return reject(new Error('Fera not loaded'));
      setTimeout(tick, 100);
    };
    tick();
  });
}

function applyFeraRating(el, rating) {
  if (!rating || typeof rating.average !== 'number' || rating.average < 4) {
    el.style.display = 'none';
    el.innerHTML = '&nbsp;';
    return;
  }
  const filled = Math.round(rating.average);
  let stars = '';
  for (let i = 0; i < 5; i++) {
    stars += i < filled ? '★' : '☆';
  }
  // Hide the count entirely if there are fewer than 10 reviews
  const showCount = rating.count != null && rating.count >= 10;
  const countText = showCount
    ? `${rating.count} review${rating.count === 1 ? '' : 's'}`
    : '';
  el.innerHTML =
    `<span style="color:#9D503E;font-size:11px;line-height:1;letter-spacing:0.5px;">${stars}</span>` +
    (countText ? `<span style="font-size:10px;color:#8b7355;font-family:'Harmonia Sans W01 Regular',Helvetica,Arial,sans-serif;">${countText}</span>` : '');
  el.style.display = 'flex';
}

// Hydrate Fera ratings for cart line items (which are not inside <cart-recommendations>)
function hydrateLineItemRatings(scope) {
  const root = scope || document;
  const placeholders = root.querySelectorAll('.cart-item-rating[data-fera-rating]');
  if (placeholders.length === 0) return;

  // Paint cached values immediately so stars don't flash off during cart updates.
  placeholders.forEach(el => {
    const productId = el.dataset.productId;
    if (!productId) return;
    if (Object.prototype.hasOwnProperty.call(FERA_RATING_CACHE, productId)) {
      applyFeraRating(el, FERA_RATING_CACHE[productId]);
    }
  });

  waitForFera()
    .then(fera => {
      if (!fera || !fera.api || typeof fera.api.retrieveProductRating !== 'function') return;
      placeholders.forEach(el => {
        const productId = el.dataset.productId;
        if (!productId) return;
        fera.api
          .retrieveProductRating(productId)
          .then(rating => {
            FERA_RATING_CACHE[productId] = rating || null;
            applyFeraRating(el, rating);
          })
          .catch(() => {});
      });
    })
    .catch(() => {});
}

// Run on initial cart drawer load and on every cart change
document.addEventListener('DOMContentLoaded', () => hydrateLineItemRatings());
document.addEventListener('cart:change', () => hydrateLineItemRatings());

// Floating "Item added" toast that sits above everything (including the cart
// drawer overlay) and survives the cart drawer body re-render that happens
// straight after a + Add click. Auto-dismisses.
function showCartToast(message) {
  // Reuse if one is already on screen — replace text and reset timer.
  let toast = document.querySelector('.fbt-cart-toast');
  if (!toast) {
    toast = document.createElement('div');
    toast.className = 'fbt-cart-toast';
    toast.style.cssText =
      'position:fixed;top:20px;left:50%;' +
      'transform:translateX(-50%) translateY(-12px);' +
      'background:#5c4a35;color:#fff;padding:12px 18px;border-radius:6px;' +
      'font-size:13px;font-family:\'Harmonia Sans W01 Regular\',Helvetica,Arial,sans-serif;' +
      'z-index:2147483647;opacity:0;' +
      'transition:opacity 220ms ease, transform 220ms ease;' +
      'box-shadow:0 6px 18px rgba(0,0,0,0.18);' +
      'display:flex;align-items:center;gap:10px;pointer-events:none;' +
      'max-width:calc(100vw - 40px);';
    document.body.appendChild(toast);
  }
  toast.innerHTML =
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">' +
    '<circle cx="12" cy="12" r="10"></circle><polyline points="9 12 11 14 15 10"></polyline></svg>' +
    '<span>' + message + '</span>';

  // Trigger fade-in
  requestAnimationFrame(() => {
    toast.style.opacity = '1';
    toast.style.transform = 'translateX(-50%) translateY(0)';
  });

  // Reset auto-dismiss timer if one was already running
  if (toast._dismissTimer) clearTimeout(toast._dismissTimer);
  toast._dismissTimer = setTimeout(() => {
    toast.style.opacity = '0';
    toast.style.transform = 'translateX(-50%) translateY(-12px)';
    setTimeout(() => {
      if (toast.parentNode) toast.remove();
    }, 260);
  }, 2500);
}

// Mirrors snippets/estimated-shipping.liquid so the line item Pre-order property
// added via FBT matches the regular product page format exactly.
function formatEstimatedShippingWindow(fromIso, toIso) {
  if (!fromIso || !toIso) return '';
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const ordinal = day => {
    const lastTwo = day % 100;
    if (lastTwo === 11 || lastTwo === 12 || lastTwo === 13) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };
  const parse = iso => {
    const parts = iso.split('-');
    if (parts.length < 3) return null;
    return { month: months[parseInt(parts[1], 10) - 1], day: parseInt(parts[2], 10) };
  };
  const f = parse(fromIso);
  const t = parse(toIso);
  if (!f || !t) return '';
  return `Estimated shipping window between ${f.month} ${f.day}${ordinal(f.day)} and ${t.month} ${t.day}${ordinal(t.day)}`;
}

customElements.define('cart-recommendations', CartRecommendations);
